/* @jsx h */
import { computed, defineAsyncComponent, defineComponent, h, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { setTitle } from "@lemmonhq/orders";

import { formatAdminPrice } from "../helpers";
import {
  type DateRange,
  DateRangePicker,
  getDateRange,
  Loading,
  ListMultiSelect,
} from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth } from "@app/vue/providers/Auth";
import { AdminMetricCard } from "../components/AdminMetricCard";
import { AdminRestaurantRevenueTable } from "../components/AdminRestaurantRevenueTable";

const AdminDashboardRevenueChart = defineAsyncComponent(
  () => import("../components/AdminDashboardRevenueChart"),
);

const rangeOptions = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "last_12m", label: "Last 12 months" },
  { value: "custom", label: "Custom" },
] as const;

export const AdminDashboardPage = defineComponent({
  name: "AdminDashboardPage",
  setup() {
    setTitle("Admin Dashboard");

    const auth = useAuth();
    const trpc = useTrpc();
    const selectedRestaurantIds = ref<number[]>([]);
    const selectedRange = ref<DateRange>({
      ...getDateRange("this_year"),
      type: "this_year" as const,
    });

    const queryKey = computed(() => [
      "admin",
      "dashboard",
      selectedRange.value.startDate,
      selectedRange.value.endDate,
      [...selectedRestaurantIds.value].sort((a, b) => a - b),
    ]);

    const dashboardQuery = useQuery({
      queryKey,
      enabled: computed(() => auth.user.value?.role === "admin"),
      queryFn: () =>
        trpc.admin.dashboard.query({
          startDate: selectedRange.value.startDate,
          endDate: selectedRange.value.endDate,
          restaurantIds: selectedRestaurantIds.value,
        }),
    });

    const restaurants = computed(() =>
      (dashboardQuery.data.value?.restaurants || []).map((restaurant) => ({
        id: restaurant.id,
        name: restaurant.name,
      })),
    );

    const chartDates = computed(() =>
      (dashboardQuery.data.value?.timeseries || []).map(
        (item) => new Date(item.bucket_date),
      ),
    );

    const chartCategories = computed(() =>
      (dashboardQuery.data.value?.timeseries || []).map((item) =>
        new Date(item.bucket_date).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      ),
    );

    const chartTotals = computed(() =>
      (dashboardQuery.data.value?.timeseries || []).map((item) => item.gmv),
    );

    const activeRestaurantsLabel = computed(() => {
      const count =
        dashboardQuery.data.value?.summary.active_restaurants_count || 0;
      return `${count} restaurants in scope`;
    });

    return () => {
      if (auth.user.value?.role !== "admin") {
        return (
          <div class="flex h-full flex-1 items-center justify-center p-6">
            <div class="rounded-2xl border border-base-300 bg-base-100 px-8 py-10 text-center shadow-sm">
              <div class="text-lg font-semibold">Admin access required</div>
              <div class="mt-2 text-sm text-base-content/60">
                This area is available only to admin users.
              </div>
            </div>
          </div>
        );
      }

      const summary = dashboardQuery.data.value?.summary;
      const breakdown = dashboardQuery.data.value?.breakdown || [];

      return (
        <div class="flex min-h-0 w-full flex-col overflow-y-auto overflow-x-hidden">
          <div class="sticky top-0 z-[1] border-b border-base-300/60 bg-base-100/95 px-4 py-4 backdrop-blur">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div class="mb-2 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-accent">
                  <span class="material-symbols-outlined text-sm">
                    shield_person
                  </span>
                  Admin analytics
                </div>
                <h2 class="text-2xl font-semibold text-accent">
                  Platform dashboard
                </h2>
                <p class="text-sm text-base-content/60">
                  Cumulative GMV across restaurants with payment and reservation
                  context.
                </p>
              </div>

              <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                <ListMultiSelect
                  items={restaurants.value}
                  selectedIds={selectedRestaurantIds.value}
                  onChange={(ids) => {
                    selectedRestaurantIds.value = ids;
                  }}
                  loading={dashboardQuery.isPending.value}
                  buttonLabel="Restaurants"
                  searchPlaceholder="Search restaurants..."
                />

                <DateRangePicker
                  value={selectedRange.value}
                  options={rangeOptions as any}
                  onChange={(range) => {
                    selectedRange.value = range;
                  }}
                  className=""
                />

                <button
                  class="btn btn-outline"
                  onClick={() => dashboardQuery.refetch()}
                >
                  <span class="material-symbols-outlined text-base">
                    refresh
                  </span>
                </button>
              </div>
            </div>
          </div>

          {dashboardQuery.isPending.value ? (
            <div class="flex flex-1 items-center justify-center p-10">
              <Loading />
            </div>
          ) : dashboardQuery.error.value || !summary ? (
            <div class="flex flex-1 items-center justify-center p-10">
              <div class="rounded-2xl border border-base-300 bg-base-100 px-8 py-10 text-center shadow-sm">
                <div class="text-lg font-semibold">
                  Failed to load dashboard
                </div>
                <div class="mt-2 text-sm text-base-content/60">
                  Try refreshing the dashboard query.
                </div>
              </div>
            </div>
          ) : (
            <div class="flex flex-col gap-6 p-4">
              <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <AdminMetricCard
                  title="GMV"
                  icon="payments"
                  value={formatAdminPrice(summary.gmv.current, false, 2)}
                  subtitle={activeRestaurantsLabel.value}
                  current={summary.gmv.current}
                  previous={summary.gmv.previous}
                />
                <AdminMetricCard
                  title="Payments"
                  icon="receipt_long"
                  value={summary.payments_count.current}
                  subtitle={`${summary.payments_count.previous} last period`}
                  current={summary.payments_count.current}
                  previous={summary.payments_count.previous}
                />
                <AdminMetricCard
                  title="Reservations"
                  icon="calendar_month"
                  value={summary.reservations_count.current}
                  subtitle={`${summary.reservations_count.previous} last period`}
                  current={summary.reservations_count.current}
                  previous={summary.reservations_count.previous}
                />
                <AdminMetricCard
                  title="Tips"
                  icon="savings"
                  value={formatAdminPrice(summary.tips.current, false, 2)}
                  subtitle={formatAdminPrice(summary.tips.previous, false, 2)}
                  current={summary.tips.current}
                  previous={summary.tips.previous}
                />
              </div>

              <div class="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
                <div class="relative overflow-hidden rounded-2xl border border-base-300/60 bg-gradient-to-br from-base-100 to-base-200/40 p-5 shadow-sm">
                  <div class="mb-4">
                    <h3 class="text-lg font-semibold text-accent">GMV trend</h3>
                    <p class="text-sm text-base-content/60">
                      Daily GMV for the selected restaurants and period.
                    </p>
                  </div>
                  <div class="relative min-h-[360px]">
                    <AdminDashboardRevenueChart
                      dates={chartDates.value}
                      categories={chartCategories.value}
                      series={[
                        {
                          key: "gmv",
                          name: "GMV",
                          data: chartTotals.value,
                          gradient: ["#34d399", "#059669"] as const,
                          line: "#10b981",
                        },
                      ]}
                      activeSeriesKeys={["gmv"]}
                      totalSeries={chartTotals.value}
                      loading={dashboardQuery.isFetching.value}
                      groupBy="day"
                    />
                  </div>
                </div>

                <div class="rounded-2xl border border-base-300/60 bg-gradient-to-br from-base-100 to-base-200/40 p-5 shadow-sm">
                  <div class="mb-4">
                    <h3 class="text-lg font-semibold text-accent">
                      Payment mix
                    </h3>
                    <p class="text-sm text-base-content/60">
                      Current period totals by payment method.
                    </p>
                  </div>

                  <div class="space-y-3">
                    {[
                      ["Cash", summary.cash_amount.current],
                      ["Card", summary.card_amount.current],
                      ["Twint", summary.twint_amount.current],
                      ["Online", summary.online_amount.current],
                      ["Manual", summary.manual_amount.current],
                    ].map(([label, value]) => (
                      <div
                        key={String(label)}
                        class="rounded-xl border border-base-300/50 bg-base-200/50 px-4 py-3"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <span class="text-sm font-medium text-accent">
                            {label}
                          </span>
                          <span class="font-medium text-base-content">
                            {formatAdminPrice(Number(value), false, 2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <AdminRestaurantRevenueTable rows={breakdown} />
            </div>
          )}
        </div>
      );
    };
  },
});
